/**
 * Context para compartir estado global del módulo de soporte
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 6.1
 */

import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { ErpContext } from './ErpContext';

export const SoporteContext = createContext();

/**
 * Provider del contexto de soporte
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componentes hijos
 * @returns {JSX.Element}
 */
export const SoporteProvider = ({ children }) => {
  const { serverURL } = useContext(ErpContext);
  
  // Debug: Verificar serverURL
  useEffect(() => {
    console.log('🔍 [SoporteContext] serverURL recibido:', serverURL);
    if (!serverURL) {
      console.error('❌ [SoporteContext] serverURL es undefined o null!');
    }
  }, [serverURL]);
  
  // Estado del contexto
  const [categorias, setCategorias] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  /**
   * Cargar datos comunes del módulo de soporte
   */
  const cargarDatosComunes = async () => {
    // Validar serverURL antes de continuar
    if (!serverURL) {
      console.error('❌ [SoporteContext] No se puede cargar datos: serverURL no está definido');
      setError('Error de configuración: URL del servidor no definida');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // Obtener rol del usuario para determinar qué cargar (antes del try para usarlo en catch)
    const userRole = Cookies.get('rolUsuario') || '';
    const esAgente = userRole === 'Agente Soporte' || userRole === 'Soporte';
    const esAdmin = userRole === 'Administrador';
    const puedeVerConfig = esAgente || esAdmin;
    
    try {
      console.log('🔄 [SoporteContext] Iniciando carga de datos...');
      console.log('🔍 [SoporteContext] serverURL:', serverURL);
      console.log('🔍 [SoporteContext] Puede ver config:', puedeVerConfig, '(Rol:', userRole, ')');
      
      // Construir URLs completas
      const urlCategorias = `${serverURL}/soporteAPI/categorias`;
      const urlPrioridades = `${serverURL}/soporteAPI/prioridades`;
      const urlConfig = `${serverURL}/soporteAPI/config`;
      
      console.log('🔗 [SoporteContext] URL categorías:', urlCategorias);
      console.log('🔗 [SoporteContext] URL prioridades:', urlPrioridades);
      console.log('🔗 [SoporteContext] URL config:', urlConfig);
      
      // Cargar categorías y prioridades siempre, configuración solo si es agente/admin
      const promesas = [
        axios.get(urlCategorias, { withCredentials: true }),
        axios.get(urlPrioridades, { withCredentials: true })
      ];
      
      if (puedeVerConfig) {
        promesas.push(axios.get(urlConfig, { withCredentials: true }));
      }
      
      const resultados = await Promise.all(promesas);
      const categoriasRes = resultados[0];
      const prioridadesRes = resultados[1];
      const configRes = puedeVerConfig ? resultados[2] : null;
      
      console.log('📦 [SoporteContext] Respuesta categorías:', categoriasRes.data);
      console.log('📦 [SoporteContext] Respuesta prioridades:', prioridadesRes.data);
      if (configRes) {
        console.log('📦 [SoporteContext] Respuesta config:', configRes.data);
      } else {
        console.log('📦 [SoporteContext] Config no cargada (usuario normal)');
      }
      
      // Filtrar solo categorías activas y no eliminadas
      const categoriasActivas = (categoriasRes.data.categorias || categoriasRes.data || [])
        .filter(c => c.activa && !c.eliminado);
      
      // Filtrar solo prioridades activas y no eliminadas
      const prioridadesActivas = (prioridadesRes.data.prioridades || prioridadesRes.data || [])
        .filter(p => p.activa && !p.eliminado);
      
      // Obtener configuración (solo si se cargó)
      const configData = configRes ? (configRes.data.config || configRes.data || null) : null;
      
      console.log('✅ [SoporteContext] Categorías activas:', categoriasActivas.length);
      console.log('✅ [SoporteContext] Prioridades activas:', prioridadesActivas.length);
      console.log('✅ [SoporteContext] Config:', configData ? 'OK' : 'null');
      
      setCategorias(categoriasActivas);
      setPrioridades(prioridadesActivas);
      setConfig(configData);
    } catch (err) {
      console.error('❌ [SoporteContext] Error cargando datos de soporte:', err);
      console.error('❌ [SoporteContext] Error response:', err.response);
      console.error('❌ [SoporteContext] Error message:', err.message);
      
      // Si el error es por falta de permisos en config, no es crítico para usuarios normales
      const esErrorConfig = err.response?.status === 403 && err.config?.url?.includes('/config');
      if (esErrorConfig && !puedeVerConfig) {
        // Error esperado para usuarios normales, no establecer error global
        console.log('ℹ️ [SoporteContext] Usuario normal sin acceso a config (esperado)');
        setError(null);
      } else {
        setError(err.response?.data?.error || err.message || 'Error al cargar datos de soporte');
      }
      
      // Establecer valores por defecto en caso de error
      setCategorias([]);
      setPrioridades([]);
      setConfig(null);
    } finally {
      setLoading(false);
      console.log('🏁 [SoporteContext] Carga completada');
    }
  };
  
  /**
   * Recargar categorías
   */
  const recargarCategorias = async () => {
    try {
      const response = await axios.get(`${serverURL}/soporteAPI/categorias`, {
        withCredentials: true
      });
      
      const categoriasActivas = (response.data.categorias || response.data || [])
        .filter(c => c.activa && !c.eliminado);
      
      setCategorias(categoriasActivas);
    } catch (err) {
      console.error('Error recargando categorías:', err);
      setError(err.response?.data?.error || 'Error al recargar categorías');
    }
  };
  
  /**
   * Recargar prioridades
   */
  const recargarPrioridades = async () => {
    try {
      const response = await axios.get(`${serverURL}/soporteAPI/prioridades`, {
        withCredentials: true
      });
      
      const prioridadesActivas = (response.data.prioridades || response.data || [])
        .filter(p => p.activa && !p.eliminado);
      
      setPrioridades(prioridadesActivas);
    } catch (err) {
      console.error('Error recargando prioridades:', err);
      setError(err.response?.data?.error || 'Error al recargar prioridades');
    }
  };
  
  /**
   * Recargar configuración
   */
  const recargarConfig = async () => {
    try {
      const response = await axios.get(`${serverURL}/soporteAPI/config`, {
        withCredentials: true
      });
      
      const configData = response.data.config || response.data || null;
      setConfig(configData);
    } catch (err) {
      console.error('Error recargando configuración:', err);
      setError(err.response?.data?.error || 'Error al recargar configuración');
    }
  };
  
  /**
   * Recargar todos los datos
   */
  const recargarTodo = async () => {
    await cargarDatosComunes();
  };
  
  // Cargar datos al montar el componente
  useEffect(() => {
    cargarDatosComunes();
  }, [serverURL]);
  
  // Valor del contexto
  const value = {
    categorias,
    prioridades,
    config,
    loading,
    error,
    recargarCategorias,
    recargarPrioridades,
    recargarConfig,
    recargarTodo
  };
  
  return (
    <SoporteContext.Provider value={value}>
      {children}
    </SoporteContext.Provider>
  );
};

